"use client";

import { AnimatePresence, motion } from "framer-motion";
import { RefreshCw, Wifi, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";

type BannerState = "online" | "offline" | "reconnected" | "hidden";

export function OfflineBanner() {
	const [state, setState] = useState<BannerState>("hidden");

	useEffect(() => {
		// Don't flash anything on initial load if already online
		if (!navigator.onLine) {
			setState("offline");
		}

		let reconnectTimer: ReturnType<typeof setTimeout>;

		const handleOffline = () => {
			clearTimeout(reconnectTimer);
			setState("offline");
		};

		const handleOnline = () => {
			clearTimeout(reconnectTimer);
			setState("reconnected");
			// Hide the "back online" banner after 3 s
			reconnectTimer = setTimeout(() => setState("hidden"), 3000);
		};

		window.addEventListener("offline", handleOffline);
		window.addEventListener("online", handleOnline);

		return () => {
			clearTimeout(reconnectTimer);
			window.removeEventListener("offline", handleOffline);
			window.removeEventListener("online", handleOnline);
		};
	}, []);

	return (
		<AnimatePresence>
			{(state === "offline" || state === "reconnected") && (
				<motion.div
					key={state}
					initial={{ y: 80, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: 80, opacity: 0 }}
					transition={{ type: "spring", stiffness: 300, damping: 30 }}
					className="fixed bottom-4 left-0 right-0 z-[9999] flex justify-center px-4 pointer-events-none"
					aria-live="assertive"
					aria-atomic="true"
				>
					{state === "offline" ? (
						<div className="pointer-events-auto flex items-center gap-3 bg-amber-950 dark:bg-amber-900 text-amber-100 px-4 py-3 rounded-2xl shadow-2xl max-w-sm w-full sm:w-auto border border-amber-800">
							<div className="shrink-0 w-8 h-8 rounded-full bg-amber-800/60 flex items-center justify-center">
								<WifiOff className="w-4 h-4" />
							</div>
							<div className="flex-1 min-w-0">
								<p className="font-semibold text-sm leading-tight">
									You're offline
								</p>
								<p className="text-xs text-amber-300 mt-0.5 leading-tight">
									Check your connection and try again
								</p>
							</div>
							<button
								type="button"
								onClick={() => window.location.reload()}
								className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-amber-800/60 hover:bg-amber-700/60 transition-colors px-3 py-1.5 rounded-xl"
								aria-label="Retry"
							>
								<RefreshCw className="w-3 h-3" />
								Retry
							</button>
						</div>
					) : (
						<div className="pointer-events-auto flex items-center gap-3 bg-emerald-950 dark:bg-emerald-900 text-emerald-100 px-4 py-3 rounded-2xl shadow-2xl max-w-sm w-full sm:w-auto border border-emerald-800">
							<div className="shrink-0 w-8 h-8 rounded-full bg-emerald-800/60 flex items-center justify-center">
								<Wifi className="w-4 h-4" />
							</div>
							<div className="flex-1 min-w-0">
								<p className="font-semibold text-sm leading-tight">
									Back online!
								</p>
								<p className="text-xs text-emerald-300 mt-0.5 leading-tight">
									Your connection has been restored
								</p>
							</div>
						</div>
					)}
				</motion.div>
			)}
		</AnimatePresence>
	);
}
